import { Box, Typography } from "@mui/material";
import { useState } from "react";

import useAPI from "../../../../../../api/hooks/useAPI";
import TextWithExternalLink from "../../../../../../components/ExternalLink";
import { ComplexEntityTypes } from "../../../../../../utils/constants";
import { canonicalizeEmail } from "../../../../../../utils/utils";
import { useEntities } from "../../../hooks/useEntities";
import EntityDrawer from "../../EntityDrawer/EntityDrawer";
import TextIntegrationStyle from "../../TextIntegrationStyle";

const SalesforceAppDetailsView = ({ entityData }) => {
  const [entityForDrawer, setEntityForDrawer] = useState(false);
  const { useQueryForEntitiesByType } = useEntities();

  const { data: userEntities } = useQueryForEntitiesByType({
    type: ComplexEntityTypes.USER,
    enabled: Boolean(entityData?.salesforce_data?.created_by_user_email),
  });

  const { trackEvent } = useAPI();

  const userEntity =
    userEntities &&
    entityData?.salesforce_data?.created_by_user_email &&
    userEntities.find(
      (entity) =>
        entity.unique_id ===
        canonicalizeEmail(entityData.salesforce_data?.created_by_user_email)
    );

  const userDisplayName = userEntity
    ? userEntity.display_name || userEntity.original_unique_id
    : entityData?.salesforce_data?.created_by_user_name ||
      entityData?.salesforce_data?.created_by_user_email;

  const onUserEmailClick = () => {
    trackEvent(
      `User clicked on user email ${entityData?.salesforce_data?.created_by_user_email}, from app details view ${entityData?.display_name}`
    );
    setEntityForDrawer(userEntity);
  };

  return (
    <Box>
      <Box display="flex" flexDirection="column" gap={2}>
        {entityData?.salesforce_data?.app_type && (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Typography variant="subtitle2">Application Type</Typography>
            </Box>
            <Box ml={2}>
              <TextIntegrationStyle
                title=""
                value={entityData.salesforce_data.app_type}
              />
            </Box>
          </Box>
        )}
        {entityData?.salesforce_data?.created_by_user_email && (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Typography variant="subtitle2">Created By</Typography>
            </Box>
            <Box ml={2} display="flex">
              <TextIntegrationStyle
                title=""
                value={userDisplayName}
                onClick={userEntity ? onUserEmailClick : undefined}
                style={
                  userEntity
                    ? {
                        cursor: "pointer",
                        textDecoration: "underline",
                      }
                    : {}
                }
                tooltipTitle={
                  userEntity ? "Click to view user details" : undefined
                }
              />
            </Box>
          </Box>
        )}
        {entityData?.salesforce_data?.admin_approved_users_only !==
          undefined && (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Typography variant="subtitle2">Permitted Users</Typography>
            </Box>
            <Box ml={2}>
              <TextIntegrationStyle
                title=""
                value={
                  entityData.salesforce_data.admin_approved_users_only
                    ? "Admin approved users are pre-authorized"
                    : "All users may self-authorize"
                }
              />
            </Box>
          </Box>
        )}
        {entityData?.salesforce_data?.has_session_policy !== undefined && (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Typography variant="subtitle2">Has Session Policy</Typography>
            </Box>
            <Box ml={2}>
              <TextIntegrationStyle
                title=""
                value={
                  entityData.salesforce_data.has_session_policy ? "Yes" : "No"
                }
              />
            </Box>
          </Box>
        )}
        {entityData?.salesforce_data?.info_url && (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Typography variant="subtitle2">Info URL</Typography>
            </Box>
            <Box ml={2}>
              <TextWithExternalLink
                text=""
                linkText="Application Info URL"
                href={entityData?.salesforce_data?.info_url}
              />
            </Box>
          </Box>
        )}
        {entityData?.salesforce_data?.pin_length &&
          entityData?.salesforce_data?.mobile_session_timeout && (
            <Box display="flex" flexDirection="column" gap={1}>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={1}
              >
                <Typography variant="subtitle2">Pin Length</Typography>
              </Box>
              <Box ml={2}>
                <TextIntegrationStyle
                  title=""
                  value={entityData.salesforce_data.pin_length}
                />
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={1}
              >
                <Typography variant="subtitle2">
                  Mobile Session Timeout
                </Typography>
              </Box>
              <Box ml={2}>
                <TextIntegrationStyle
                  title=""
                  value={`${entityData.salesforce_data.mobile_session_timeout} Minutes`}
                />
              </Box>
            </Box>
          )}
      </Box>
      <EntityDrawer
        openDrawer={entityForDrawer}
        selectedEntity={entityForDrawer}
        onClose={() => {
          setEntityForDrawer(null);
        }}
      />
    </Box>
  );
};

export default SalesforceAppDetailsView;
